'use client';

import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { calculateContentSize } from "@/lib/utils/contentSize";

interface UseDraggableOptions {
  initialPosition: { x: number; y: number };
  allowDragging: boolean;
  constrainingFunction?: (pos: { x: number; y: number }, size: { width: number; height: number }) => { x: number; y: number };
  size: { width: number; height: number };
  isOnLandingPage: boolean;
}

export function useDraggable({ initialPosition, allowDragging, constrainingFunction, size, isOnLandingPage }: UseDraggableOptions) {
  const [position, setPosition] = useState(initialPosition);
  const positionRef = useRef(initialPosition);
  const targetPositionRef = useRef(initialPosition);
  const animationFrameRef = useRef<number | null>(null);
  const [isPointerDown, setIsPointerDown] = useState(false);
  const isDraggingRef = useRef(false);

  // Seed the "previous" baseline from a fresh calculation. We do not read this
  // from SizeProvider context, because the context update is scheduled by
  // Desktop's resize handler and would be one tick behind the resize event that
  // also fires this hook's handler. Reading window.* directly + the shared
  // calculateContentSize keeps the handler in a single coordinate system.
  const prevWidthContentRef = useRef(0);
  const prevHeightContentRef = useRef(0);
  const prevWindowWidthRef = useRef(0);
  const prevWindowHeightRef = useRef(0);

  useEffect(() => {
    const seed = calculateContentSize();
    prevWidthContentRef.current = seed.width;
    prevHeightContentRef.current = seed.height;
    prevWindowWidthRef.current = window.innerWidth;
    prevWindowHeightRef.current = window.innerHeight;
  }, []);

  positionRef.current = position;

  const [prevInitialPosition, setPrevInitialPosition] = useState(initialPosition);
  if (
    prevInitialPosition.x !== initialPosition.x ||
    prevInitialPosition.y !== initialPosition.y
  ) {
    setPosition(initialPosition);
    setPrevInitialPosition(initialPosition);
  }

  // Cleanup animation frame on unmount
  useEffect(() => {
    return () => {
      // console.log('cleaning up draggable hook')
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, []);

  // Handle window resize and pointer up events
  useEffect(() => {
    const handleWindowPointerUp = () => {
      setIsPointerDown(false);
      isDraggingRef.current = false;
    };

    // Reposition self, based on previous position within content area.
    const onWindowResizeLandingPage = () => {
      const prevWidthContent = prevWidthContentRef.current;
      const prevWindowWidth = prevWindowWidthRef.current;
      const prevHeightContent = prevHeightContentRef.current;
      const prevWindowHeight = prevWindowHeightRef.current;

      const prevLeftOffset = Math.floor((prevWindowWidth - prevWidthContent) / 2);

      const contentPercentagePositionX = prevWidthContent > 0 ? (positionRef.current.x - prevLeftOffset) / prevWidthContent : 0;
      const contentPercentagePositionY = prevHeightContent > 0 ? (positionRef.current.y - prevWindowHeight) / prevHeightContent : 0;

      const { width: nextWidthContent, height: nextHeightContent } = calculateContentSize();
      const nextWindowWidth = window.innerWidth;
      const nextWindowHeight = window.innerHeight;
      const nextLeftOffset = Math.floor((nextWindowWidth - nextWidthContent) / 2);
      const targetPositionX = nextLeftOffset + contentPercentagePositionX * nextWidthContent;
      const targetPositionY = nextWindowHeight + contentPercentagePositionY * nextHeightContent;

      if (constrainingFunction) {
        const constrainedPosition = constrainingFunction({ x: targetPositionX, y: targetPositionY }, { width: size.width, height: size.height });
        setPosition(constrainedPosition);
      }
      else {
        setPosition({ x: targetPositionX, y: targetPositionY });
      }

      prevWidthContentRef.current = nextWidthContent;
      prevWindowWidthRef.current = nextWindowWidth;
      prevHeightContentRef.current = nextHeightContent;
      prevWindowHeightRef.current = nextWindowHeight;
    };

    // Reposition self, based on previous position within content area.
    const onWindowResizeProjectDetails = () => {
      const prevWidthContent = prevWidthContentRef.current;
      const prevWindowWidth = prevWindowWidthRef.current;
      const prevHeightContent = prevHeightContentRef.current;
      const prevWindowHeight = prevWindowHeightRef.current;

      const prevLeftOffset = Math.floor((prevWindowWidth - prevWidthContent) / 2);
      const prevTopOffset = Math.floor((prevWindowHeight - prevHeightContent) / 2);

      const contentPercentagePositionX = prevWidthContent > 0 ? (positionRef.current.x - prevLeftOffset) / prevWidthContent : 0;
      const contentPercentagePositionY = prevHeightContent > 0 ? (positionRef.current.y - prevTopOffset) / prevHeightContent : 0;

      const { width: nextWidthContent, height: nextHeightContent } = calculateContentSize();
      const nextWindowWidth = window.innerWidth;
      const nextWindowHeight = window.innerHeight;
      const nextLeftOffset = Math.floor((nextWindowWidth - nextWidthContent) / 2);
      const nextTopOffset = Math.floor((nextWindowHeight - nextHeightContent) / 2);
      const targetPositionX = nextLeftOffset + contentPercentagePositionX * nextWidthContent;
      const targetPositionY = nextTopOffset + contentPercentagePositionY * nextHeightContent;

      if (constrainingFunction) {
        const constrainedPosition = constrainingFunction({ x: targetPositionX, y: targetPositionY }, { width: size.width, height: size.height });
        setPosition(constrainedPosition);
      }
      else {
        setPosition({ x: targetPositionX, y: targetPositionY });
      }

      prevWidthContentRef.current = nextWidthContent;
      prevWindowWidthRef.current = nextWindowWidth;
      prevHeightContentRef.current = nextHeightContent;
      prevWindowHeightRef.current = nextWindowHeight;
    };

    // Subscribing to the appropriate resize function via Props
    if (isOnLandingPage) {
      window.addEventListener('resize', onWindowResizeLandingPage);
    }
    else {
      window.addEventListener('resize', onWindowResizeProjectDetails);
    }
    window.addEventListener('pointerup', handleWindowPointerUp);

    return () => {
      window.removeEventListener('pointerup', handleWindowPointerUp);
      window.removeEventListener('resize', onWindowResizeLandingPage);
      window.removeEventListener('resize', onWindowResizeProjectDetails);
    };
  }, [isPointerDown]);

  // Lerp window to targetPosition
  const lerpWindowPosition = () => {
    const current = positionRef.current;
    const target = targetPositionRef.current;
    const lerp = 0.15;

    const newX = current.x + (target.x - current.x) * lerp;
    const newY = current.y + (target.y - current.y) * lerp;

    const newPosition = { x: newX, y: newY };
    setPosition(newPosition);
    positionRef.current = newPosition;

    if (Math.abs(target.x - newX) > 0.1 || Math.abs(target.y - newY) > 0.1) {
      animationFrameRef.current = requestAnimationFrame(lerpWindowPosition);
    } 
    else {
      animationFrameRef.current = null;
    }
  };

  // Handle dragging
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    setIsPointerDown(true);

    if (allowDragging) {
      event.preventDefault();
      const grabOffsetX = positionRef.current.x - event.clientX;
      const grabOffsetY = positionRef.current.y - event.clientY;
      
      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        isDraggingRef.current = true;
        const unconstrained = {
          x: moveEvent.clientX + grabOffsetX,
          y: moveEvent.clientY + grabOffsetY,
        };

        if (constrainingFunction) {
          targetPositionRef.current = constrainingFunction(unconstrained, size);
        } else {
          targetPositionRef.current = unconstrained;
        }

        if (animationFrameRef.current === null) {
          animationFrameRef.current = requestAnimationFrame(lerpWindowPosition);
        }
      };

      const handlePointerUp = () => {
        if (target.hasPointerCapture(event.pointerId)) {
          target.releasePointerCapture(event.pointerId);
        }
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerUp);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerUp);
    }
  };

  return {
    position,
    isPointerDown,
    isDraggingRef,
    onPointerDown,
  };
}